import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, Store, Building, User, DollarSign, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import Step0VendorType from '@/components/vendor-onboarding/Step0VendorType';
import Step1BusinessInfo from '@/components/vendor-onboarding/Step1BusinessInfo';
import Step2Profile from '@/components/vendor-onboarding/Step2Profile';
import Step3Membership from '@/components/vendor-onboarding/Step3Membership';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { id: 'type', title: 'Vendor Type', icon: Store },
  { id: 'info', title: 'Business Info', icon: Building },
  { id: 'profile', title: 'Profile', icon: User },
  { id: 'membership', title: 'Membership', icon: DollarSign },
];

export default function VendorOnboarding() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  // ── Step 0 ──
  const [vendorType, setVendorType] = useState('startup');
  const [einNumber, setEinNumber] = useState('');
  const [nonprofitUrl, setNonprofitUrl] = useState('');

  // ── Step 1 ──
  const [ownerName, setOwnerName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [mainCategory, setMainCategory] = useState('');
  const [showCustomService, setShowCustomService] = useState(false);
  const [customServiceName, setCustomServiceName] = useState('');
  const [submittingCustom, setSubmittingCustom] = useState(false);
  const [location, setLocation] = useState('');
  const [locationVerified, setLocationVerified] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [tribalAffiliation, setTribalAffiliation] = useState('');
  const [ethnicity, setEthnicity] = useState([]);
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');

  // ── Step 2 ──
  const [description, setDescription] = useState('');
  const [bio, setBio] = useState('');

  // ── Step 3 ──
  const [membershipLevel, setMembershipLevel] = useState('basic');
  const [agreedToVendorTerms, setAgreedToVendorTerms] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (user?.name) setOwnerName(user.name);
    // Pre-fill ethnicity + age from signup — don't re-ask the same questions.
    if (user?.ethnicity) {
      const arr = Array.isArray(user.ethnicity) ? user.ethnicity : [user.ethnicity];
      if (arr.length) setEthnicity(arr);
    }
    if (user?.age) setAge(String(user.age));
  }, [user]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories`);
      if (response.ok) {
        const raw = await response.json();
        const sorted = [...raw]
          .map((c) => ({
            ...c,
            subcategories: [...(c.subcategories || [])].sort((a, b) =>
              (a.name || '').localeCompare(b.name || '')
            ),
          }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(sorted);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchLocationSuggestions = async (query) => {
    try {
      // City-level only: request address details and filter to cities/towns/villages/states.
      // Nominatim's addresstype tells us the kind of place (city/town/village/state/county/...).
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(query)}&limit=8&countrycodes=us,ca`);
      if (!res.ok) return setLocationSuggestions([]);
      const data = await res.json();
      const CITY_TYPES = new Set(['city', 'town', 'village', 'hamlet', 'municipality', 'suburb', 'neighbourhood', 'state', 'province']);
      const filtered = data
        .filter(d => CITY_TYPES.has((d.addresstype || d.type || '').toLowerCase()) || (d.class === 'place'))
        .map(d => {
          const a = d.address || {};
          const city = a.city || a.town || a.village || a.hamlet || a.municipality || a.suburb || a.county || '';
          const state = a.state || a.province || '';
          const country = a.country_code ? a.country_code.toUpperCase() : '';
          const display = [city, state, country].filter(Boolean).join(', ');
          return display ? { display, full: display } : null;
        })
        .filter(Boolean)
        // dedupe
        .filter((v, i, arr) => arr.findIndex(x => x.display === v.display) === i)
        .slice(0, 5);
      setLocationSuggestions(filtered);
    } catch {
      setLocationSuggestions([]);
    }
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories((prev) => (prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]));
  };

  const submitCustomService = async () => {
    if (!customServiceName.trim()) return;
    setSubmittingCustom(true);
    try {
      const res = await fetch(`${API}/categories/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: customServiceName.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.rejected) {
          toast.error(data.reason || 'This service type is not allowed on our platform.');
        } else {
          setCategories((prev) =>
            [...prev, data.category].sort((a, b) =>
              (a.name || '').localeCompare(b.name || '')
            )
          );
          setSelectedCategories((prev) => [...prev, data.category.category_id]);
          setCustomServiceName('');
          setShowCustomService(false);
          toast.success('Service added! You can now select it.');
        }
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to add service');
      }
    } catch {
      toast.error('Failed to add service');
    } finally {
      setSubmittingCustom(false);
    }
  };

  const nextStep = () => { if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (tribalAffiliation.trim()) {
        await fetch(`${API}/users/tribal-info`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ tribal_affiliation: tribalAffiliation, federally_recognized: false }),
        });
      }
      if (bio) {
        await fetch(`${API}/users/bio`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ bio }),
        });
      }
      const response = await fetch(`${API}/vendors/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          owner_name: ownerName,
          business_name: businessName || null,
          ein_number: vendorType === 'verified' ? einNumber : null,
          description,
          category_id: mainCategory || selectedCategories[0] || '',
          categories: selectedCategories,
          location,
          vendor_type: vendorType,
          nonprofit_url: vendorType === 'nonprofit' ? nonprofitUrl : null,
          membership_level: membershipLevel,
          tribal_affiliation: tribalAffiliation || null,
          ethnicity: ethnicity.length ? ethnicity : null,
          age: age ? Number(age) : null,
          gender: gender || null,
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create vendor profile');
      }
      toast.success('Vendor profile created!');
      navigate('/dashboard/vendor');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return vendorType && (
          vendorType === 'startup'
          || (vendorType === 'verified' && einNumber.trim().length >= 9)
          || (vendorType === 'nonprofit' && einNumber.trim().length >= 9)
        );
      case 1:
        return ownerName && selectedCategories.length > 0 && mainCategory && location && ethnicity.length > 0 && age && gender;
      case 2:
        return description;
      case 3:
        return membershipLevel && agreedToVendorTerms;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-stone-950">
      <Navbar />
      <main className="pt-24 pb-32 sm:pb-16">
        <div className="container-main max-w-3xl">
          {/* Progress bar */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    index <= currentStep ? 'bg-yellow-400 text-black' : 'bg-stone-800 text-stone-500'
                  }`}>
                    {index < currentStep ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`h-1 w-8 md:w-16 mx-1 rounded transition-colors ${
                      index < currentStep ? 'bg-yellow-400' : 'bg-stone-800'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <h1 className="font-syne text-2xl md:text-3xl font-bold text-stone-100 text-center" data-testid="onboarding-title">
              {steps[currentStep].title}
            </h1>
          </div>

          {/* Step body */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="card-vendor"
          >
            {currentStep === 0 && (
              <Step0VendorType
                vendorType={vendorType}
                setVendorType={setVendorType}
                einNumber={einNumber}
                setEinNumber={setEinNumber}
                nonprofitUrl={nonprofitUrl}
                setNonprofitUrl={setNonprofitUrl}
              />
            )}

            {currentStep === 1 && (
              <Step1BusinessInfo
                ethnicity={ethnicity}
                setEthnicity={setEthnicity}
                tribalAffiliation={tribalAffiliation}
                setTribalAffiliation={setTribalAffiliation}
                ownerName={ownerName}
                setOwnerName={setOwnerName}
                businessName={businessName}
                setBusinessName={setBusinessName}
                age={age}
                setAge={setAge}
                gender={gender}
                setGender={setGender}
                categories={categories}
                selectedCategories={selectedCategories}
                toggleCategory={toggleCategory}
                mainCategory={mainCategory}
                setMainCategory={setMainCategory}
                showCustomService={showCustomService}
                setShowCustomService={setShowCustomService}
                customServiceName={customServiceName}
                setCustomServiceName={setCustomServiceName}
                submittingCustom={submittingCustom}
                submitCustomService={submitCustomService}
                location={location}
                setLocation={setLocation}
                locationVerified={locationVerified}
                setLocationVerified={setLocationVerified}
                locationSuggestions={locationSuggestions}
                setLocationSuggestions={setLocationSuggestions}
                fetchLocationSuggestions={fetchLocationSuggestions}
              />
            )}

            {currentStep === 2 && (
              <Step2Profile
                description={description}
                setDescription={setDescription}
                bio={bio}
                setBio={setBio}
              />
            )}

            {currentStep === 3 && (
              <Step3Membership
                membershipLevel={membershipLevel}
                setMembershipLevel={setMembershipLevel}
                agreedToVendorTerms={agreedToVendorTerms}
                setAgreedToVendorTerms={setAgreedToVendorTerms}
              />
            )}
          </motion.div>

          {/* Nav */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="btn-secondary"
              data-testid="prev-step"
            >
              <ChevronLeft className="w-5 h-5 mr-2" /> Back
            </Button>
            {currentStep < steps.length - 1 ? (
              <Button
                onClick={nextStep}
                disabled={!canProceed()}
                className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                data-testid="next-step"
              >
                Continue <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                data-testid="submit-vendor"
              >
                {loading ? 'Creating...' : 'Create Profile'} <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
